import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AuthLayer } from '@/layers/auth/AuthLayer'
import { AuthPageShell } from '@/AuthPageShell'
import { OrgResolver } from '@/layers/shell/OrgResolver'
import { ShellLayer } from '@/layers/shell/ShellLayer'
import { AddressVerificationEntry } from '@/screens/a-verify/AddressVerificationEntry'
import { registerSessionEndedHandler } from '@/data/sessionEvents'
import { getApiClient } from '@/data/apiClient'
import { setAccessToken, resetOnSignOut } from '@/data/store'
import type { components } from '@/api/generated/schema'

type OrganisationMembership = components['schemas']['OrganisationMembership']

/** Reads `#verification=<token>` from the URL fragment (REQ-NTF-007 / LLD-IDN-163 / HLD-IDN-163 —
 * never a path or a query). Mirrors `AuthLayer.tsx`'s own `readInvitationFromFragment`. */
function readVerificationFromFragment(): string | undefined {
  const match = /(?:^|[#&])verification=([^&]+)/.exec(window.location.hash)
  return match?.[1] ? decodeURIComponent(match[1]) : undefined
}

type Phase =
  | { name: 'booting' }
  | { name: 'auth'; initialScreen?: 'a-session' | 'a-changepw' }
  | { name: 'resolving-org' }
  | { name: 'shell'; orgId: string; organisations: readonly OrganisationMembership[] }

/**
 * The three layers SPA.2 names, handed off in sequence: `AuthLayer` (state-selected,
 * `LLD-SPA-041`) → `OrgResolver` (§3.1i's first-login org resolution) → `ShellLayer` (`div.shell`
 * → `app-shell`). Nothing renders more than one at a time; a phase change is a full replacement,
 * never an overlay.
 */
export function AppRoot() {
  const [phase, setPhase] = useState<Phase>({ name: 'booting' })
  const queryClient = useQueryClient()

  // §3.1j's boot sequence — "mount → (indeterminate) → silent refresh → route" — run once, never
  // retried on failure: a reload correctly loses the in-memory access token (rule 16), so `mount`
  // knows neither a token nor evidence there is no session, and rendering sign-in during this
  // window is the specific defect the spec calls out ("makes correct behaviour look like a
  // fault"). The refresh cookie carries no `org_id`, so success only proves a session, not which
  // org/workspace — `resolving-org`/`ShellLayer` still run exactly as they do after a fresh
  // sign-in. The URL itself is untouched here, so a deep link survives either outcome for free —
  // this effect only ever decides `booting` → `resolving-org` or `booting` → `auth`.
  useEffect(() => {
    let cancelled = false
    getApiClient()
      .POST('/auth/refresh', {})
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          setPhase({ name: 'auth' })
          return
        }
        setAccessToken(data.access_token)
        setPhase({ name: 'resolving-org' })
      })
      .catch(() => {
        if (!cancelled) setPhase({ name: 'auth' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  // `a-session` — "the refresh-failure state," entered from deep inside `ShellLayer`/`OrgResolver`
  // when an authenticated call comes back `401`/`NO_IDENTITY` (`data/apiClient.ts`'s own
  // `detectSessionEnded` middleware), never from a click. Subscribed only past the `auth` phase —
  // subscribing during `auth` itself would react to sign-in's own failures, which already have
  // their own handling and are not this event. Called unconditionally (rules-of-hooks) even on
  // the `/verify` early return below — `/verify` never has a session to end.
  useEffect(() => {
    if (phase.name === 'auth') return undefined
    registerSessionEndedHandler(() => {
      queryClient.clear()
      resetOnSignOut()
      setPhase({ name: 'auth', initialScreen: 'a-session' })
    })
    return () => registerSessionEndedHandler(undefined)
  }, [phase.name, queryClient])

  // `a-verify`/`a-verifyexp` — family rule A4: "outside the shell and outside authentication
  // entirely." A real, standalone entry point (previously unmounted anywhere — its own doc
  // comment said so) rather than routed through `AuthLayer`/`OrgResolver`/`ShellLayer` at all.
  // **Corrected to read the token from the URL fragment, not a query parameter** — REQ-NTF-007 /
  // LLD-IDN-163 / HLD-IDN-163 are explicit that both mailed bearer tokens (this one and the invite
  // token) must travel in the fragment: a query segment lands in ALB/WAF/CloudFront access logs,
  // in browser history and in any outbound `Referer`, none of which the request-body scrubbing
  // rule (§3.1e) reaches. Mirrors `AuthLayer.tsx`'s own `readInvitationFromFragment` exactly. The
  // link the e-mail itself carries is the link builder's own responsibility, server-side, to match.
  // Wrapped in `AuthPageShell` for the same reason the `auth` phase below is: `body` now has
  // `overflow:hidden` (`main.css`) specifically because every full-page surface is expected to
  // bound and scroll itself — a bare `AuthCard` here, unmounted anywhere until Pass 3 wires up a
  // real route, would otherwise silently clip rather than scroll the day it does.
  const verification = readVerificationFromFragment()
  if (window.location.pathname === '/verify' && verification) {
    return (
      <AuthPageShell>
        <AddressVerificationEntry verification={verification} />
      </AuthPageShell>
    )
  }

  // The indeterminate render §3.1j names: neither the app nor sign-in, for as long as the silent
  // refresh above is in flight. Rendering sign-in here is the specific defect the spec calls out —
  // a reload would flash the login form before jumping back into the app, which reads as having
  // been signed out even though rule 16 says the reload itself was correct behaviour. **A bare
  // `null` render was a second version of the same defect** for anyone using assistive tech — no
  // surface, no signal, indistinguishable from a hang — so this names a real, minimal one instead,
  // the same `role="status"`/`aria-live="polite"` shape `main.tsx`'s own pre-router boot state
  // already uses.
  if (phase.name === 'booting') {
    return (
      <div role="status" aria-live="polite">
        Loading…
      </div>
    )
  }

  if (phase.name === 'auth') {
    return (
      <AuthPageShell>
        <AuthLayer initialScreen={phase.initialScreen} onAuthenticated={() => setPhase({ name: 'resolving-org' })} />
      </AuthPageShell>
    )
  }

  if (phase.name === 'resolving-org') {
    return <OrgResolver onResolved={(orgId, organisations) => setPhase({ name: 'shell', orgId, organisations })} />
  }

  return (
    <ShellLayer
      orgId={phase.orgId}
      organisations={phase.organisations}
      onChangePassword={() => setPhase({ name: 'auth', initialScreen: 'a-changepw' })}
      onSignOut={() => {
        // Fire-and-forget, same as `ASession`'s/`AOrgblocked`'s own sign-out control
        // (`onSettled`, not `onSuccess`) — the client-side state clears regardless of whether the
        // network call itself succeeds; there is nothing useful to do differently on its error.
        void getApiClient()
          .POST('/auth/sign-out', { body: { global: false } })
          .finally(() => {
            // LLD-SPA-066: signing out drops everything the store AND the query cache hold, so
            // the next user signed in on this tab never sees a stale `['organisations']`/
            // `['workspaces', orgId]` entry served from cache instead of refetched (TC-UIX-SHL-009).
            queryClient.clear()
            resetOnSignOut()
            setPhase({ name: 'auth' })
          })
      }}
    />
  )
}
